import {
  AstroService,
  CelestialPosition,
  EclipticIntersection,
} from "../../../services/AstroService";
import { Celestials, Intersections } from "../types";

/**
 * Adapter interface matching the original AstrolabeService for compatibility
 */
interface AstrolabeType {
  date: Date;
  location: { lat: number; lon: number; height: number };
  positions: Celestials;
  intersections: Intersections;
}

/**
 * AstroServiceAdapter - Bridges between Astrolabe components and main AstroService
 *
 * This adapter converts the main AstroService responses to the format expected
 * by the Astrolabe components, maintaining compatibility while using the updated
 * backend API structure.
 */
export class AstroServiceAdapter {
  /**
   * Convert CelestialPosition array to Celestials object format
   * Maps from main service format to component-expected format
   */
  private static convertPositionsToLegacyFormat(
    positions: CelestialPosition[],
  ): Celestials {
    const celestials: Celestials = {};

    // Initialize with default values for all expected celestial bodies
    const defaultCelestials = [
      "sun",
      "moon",
      "mercury",
      "venus",
      "mars",
      "jupiter",
      "saturn",
      "uranus",
      "neptune",
      "pluto",
    ];

    defaultCelestials.forEach((name) => {
      celestials[name] = { ecliptic: { lat: 0, lon: 0 } };
    });

    // Handle case where positions might be undefined or null
    if (!positions || !Array.isArray(positions)) {
      console.warn("Positions is not an array:", positions);
      return celestials;
    }

    // Map actual positions from the service response
    positions.forEach((position) => {
      const name = position.name.toLowerCase();

      // Map common celestial body names
      let mappedName = name;
      if (name.includes("sun")) mappedName = "sun";
      else if (name.includes("moon")) mappedName = "moon";
      else if (name.includes("mercury")) mappedName = "mercury";
      else if (name.includes("venus")) mappedName = "venus";
      else if (name.includes("mars")) mappedName = "mars";
      else if (name.includes("jupiter")) mappedName = "jupiter";
      else if (name.includes("saturn")) mappedName = "saturn";
      else if (name.includes("uranus")) mappedName = "uranus";
      else if (name.includes("neptune")) mappedName = "neptune";
      else if (name.includes("pluto")) mappedName = "pluto";

      if (celestials[mappedName]) {
        // Use ecliptic coordinates if available, otherwise fall back to azimuth/altitude
        const eclipticLat = position.ecliptic_lat ?? position.altitude ?? 0;
        const eclipticLon = position.ecliptic_lon ?? position.azimuth ?? 0;

        celestials[mappedName] = {
          ecliptic: {
            lat: eclipticLat,
            lon: eclipticLon,
          },
        };
      }
    });

    return celestials;
  }

  /**
   * Convert EclipticIntersection to Intersections format
   * Maps from main service format to component-expected format
   */
  private static convertIntersectionsToLegacyFormat(
    intersections: EclipticIntersection,
  ): Intersections {
    return {
      east: intersections.ascending.azimuth || 0,
      west: intersections.descending.azimuth || 0,
    };
  }

  /**
   * Fetch ecliptic positions for the astrolabe component
   * Compatible with the original astrolabeService.fetchEclipticPositions method
   *
   * @param date - Date string in ISO format
   * @returns Promise<AstrolabeType> - Converted astrolabe data
   */
  static async fetchEclipticPositions(date: string): Promise<AstrolabeType> {
    try {
      // Validate datetime format
      if (!AstroService.isValidDate(date)) {
        throw new Error(`Invalid date format: ${date}`);
      }

      // Call main AstroService with full datetime
      const response = await AstroService.getAstrolabePositions(date);

      // Convert to legacy format expected by Astrolabe components
      const convertedData: AstrolabeType = {
        date: new Date(response.date),
        location: response.location,
        positions: this.convertPositionsToLegacyFormat(response.positions),
        intersections: this.convertIntersectionsToLegacyFormat(
          response.intersections,
        ),
      };

      return convertedData;
    } catch (error: any) {
      console.error(
        "AstroServiceAdapter: Error fetching ecliptic positions:",
        error,
      );

      // Still throw the error but with more context
      throw new Error(error.message || "Failed to fetch astrolabe positions");
    }
  }

  /**
   * Get current ecliptic positions (convenience method)
   * @returns Promise<AstrolabeType> - Current astrolabe data
   */
  static async getCurrentEclipticPositions(): Promise<AstrolabeType> {
    const currentDate = new Date().toISOString();
    return this.fetchEclipticPositions(currentDate);
  }

  /**
   * Get ecliptic positions for specific location
   * @param date - Date string in ISO format
   * @param lat - Latitude
   * @param lon - Longitude
   * @param height - Height above sea level
   * @returns Promise<AstrolabeType> - Astrolabe data for location
   */
  static async fetchEclipticPositionsForLocation(
    date: string,
    lat: number,
    lon: number,
    height: number = 0,
  ): Promise<AstrolabeType> {
    try {
      if (!AstroService.isValidDate(date)) {
        throw new Error(`Invalid date format: ${date}`);
      }

      if (!AstroService.isValidCoordinates(lat, lon)) {
        throw new Error(`Invalid coordinates: lat=${lat}, lon=${lon}`);
      }

      const response = await AstroService.getAstrolabePositions(
        date,
        lat,
        lon,
        height,
      );

      return {
        date: new Date(response.date),
        location: response.location,
        positions: this.convertPositionsToLegacyFormat(response.positions),
        intersections: this.convertIntersectionsToLegacyFormat(
          response.intersections,
        ),
      };
    } catch (error: any) {
      console.error(
        "AstroServiceAdapter: Error fetching positions for location:",
        error,
      );
      throw new Error(
        error.message || "Failed to fetch astrolabe positions for location",
      );
    }
  }

  /**
   * Check if the AstroService is available
   * @returns boolean - Service availability
   */
  static isServiceAvailable(): boolean {
    return AstroService.isAvailable();
  }

  /**
   * Validate date input for the service
   * @param date - Date string to validate
   * @returns boolean - Whether date is valid
   */
  static isValidDate(date: string): boolean {
    const dateOnly = date.split("T")[0];
    return AstroService.isValidDate(dateOnly);
  }
}

// Create a compatible instance for backward compatibility
const astroServiceAdapter = {
  fetchEclipticPositions: (date: string) =>
    AstroServiceAdapter.fetchEclipticPositions(date),
  getCurrentEclipticPositions: () =>
    AstroServiceAdapter.getCurrentEclipticPositions(),
  fetchEclipticPositionsForLocation: (
    date: string,
    lat: number,
    lon: number,
    height?: number,
  ) =>
    AstroServiceAdapter.fetchEclipticPositionsForLocation(
      date,
      lat,
      lon,
      height,
    ),
  isServiceAvailable: () => AstroServiceAdapter.isServiceAvailable(),
  isValidDate: (date: string) => AstroServiceAdapter.isValidDate(date),
};

export default astroServiceAdapter;
